import { useState, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import { useTabParams } from "@/contexts/TabParamsContext";
import { useTabLabel } from "@/hooks/useTabLabel";
import { useQuery } from "@tanstack/react-query";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCustomerContacts, useCreateCustomerContact, useDeleteCustomerContact } from "@/hooks/useCustomerContacts";
import { useCustomerNotes, useCreateCustomerNote } from "@/hooks/useCustomerNotes";
import { Customer } from "@/hooks/useCustomers";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { NewEstimateDialog } from "@/components/controller/NewEstimateDialog";
import { NewProjectWizard } from "@/components/controller/NewProjectWizard";
import { NewCustomerDialog } from "@/components/controller/NewCustomerDialog";
import { StatusBadge } from "@/components/controller/StatusBadge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowLeft, Plus, Pencil, Mail, Phone, User, Building2, ExternalLink, Camera } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

const TYPE_LABELS: Record<string, string> = {
  general_contractor: "General Contractor",
  property_manager: "Property Manager",
  owner: "Owner",
  insurance_company: "Insurance Co.",
  other: "Other",
};

const SYNC_BADGE: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  synced: { label: "Synced", variant: "default" },
  pending: { label: "Pending", variant: "secondary" },
  error: { label: "Error", variant: "destructive" },
  not_synced: { label: "Not Synced", variant: "outline" },
};

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

const CustomerDetail = () => {
  const { id } = useTabParams<{ id: string }>();
  const navigate = useTabNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "projects";
  const { user } = useAuth();

  const [editOpen, setEditOpen] = useState(false);
  const [newEstimateOpen, setNewEstimateOpen] = useState(false);
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [newContactOpen, setNewContactOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [noteText, setNoteText] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const logoRef = useRef<HTMLInputElement>(null);

  // Contact form state
  const [contactName, setContactName] = useState("");
  const [contactTitle, setContactTitle] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactReceivesEstimates, setContactReceivesEstimates] = useState(true);
  const [contactReceivesInvoices, setContactReceivesInvoices] = useState(true);
  const [contactNotes, setContactNotes] = useState("");

  // Fetch customer
  const { data: customer, isLoading } = useQuery({
    queryKey: ["customer", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customers")
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data as unknown as Customer;
    },
    enabled: !!user && !!id,
  });

  // Fetch projects for this customer
  const { data: projects } = useQuery({
    queryKey: ["customer_projects", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("*, contracts(original_value, revised_value, status)")
        .eq("customer_id", id!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
    enabled: !!user && !!id,
  });

  // Fetch estimates for this customer
  const { data: estimates } = useQuery({
    queryKey: ["customer_estimates", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estimates")
        .select("*")
        .eq("customer_id", id!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
    enabled: !!user && !!id,
  });

  // Fetch invoices across all projects for this customer
  const { data: invoices } = useQuery({
    queryKey: ["customer_invoices", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("*, projects(name)")
        .eq("customer_id", id!)
        .is("deleted_at", null)
        .order("invoice_date", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
    enabled: !!user && !!id,
  });

  const { data: contacts } = useCustomerContacts(id);
  const { data: notes } = useCustomerNotes(id);
  const createContact = useCreateCustomerContact();
  const deleteContact = useDeleteCustomerContact();
  const createNote = useCreateCustomerNote();

  useTabLabel(customer?.name ? customer.name : undefined);

  // Stats
  const stats = useMemo(() => {
    if (!projects) return { total: 0, active: 0, contractValue: 0, billed: 0, outstanding: 0 };
    const total = projects.length;
    const active = projects.filter((p: any) => p.status === "active").length;
    let contractValue = 0;
    projects.forEach((p: any) => {
      const approvedContracts = (p.contracts || []).filter((c: any) => c.status === "approved");
      approvedContracts.forEach((c: any) => { contractValue += Number(c.revised_value || c.original_value || 0); });
    });
    const billedStatuses = ["approved", "sent", "paid", "partially_paid", "overdue"];
    const billed = invoices?.filter((i: any) => billedStatuses.includes(i.status)).reduce((s: number, i: any) => s + Number(i.total || 0), 0) || 0;
    return { total, active, contractValue, billed, outstanding: contractValue - billed };
  }, [projects, invoices]);

  const filteredProjects = useMemo(() => {
    if (!projects) return [];
    if (statusFilter === "all") return projects;
    return projects.filter((p: any) => p.status === statusFilter);
  }, [projects, statusFilter]);

  const syncBadge = (status: string | null) => {
    const s = SYNC_BADGE[status || "not_synced"] || SYNC_BADGE.not_synced;
    return <Badge variant={s.variant} className="text-[10px]">{s.label}</Badge>;
  };

  const handleAddContact = async () => {
    if (!contactName.trim() || !id) return;
    try {
      await createContact.mutateAsync({
        customer_id: id,
        name: contactName,
        title: contactTitle || undefined,
        email: contactEmail || undefined,
        phone: contactPhone || undefined,
        receives_estimates: contactReceivesEstimates,
        receives_invoices: contactReceivesInvoices,
        notes: contactNotes || undefined,
      });
      toast.success("Contact added");
      setNewContactOpen(false);
      setContactName(""); setContactTitle(""); setContactEmail(""); setContactPhone("");
      setContactReceivesEstimates(true); setContactReceivesInvoices(true); setContactNotes("");
    } catch (e: any) { toast.error(e.message); }
  };

  const handleAddNote = async () => {
    if (!noteText.trim() || !id) return;
    try {
      await createNote.mutateAsync({ customer_id: id, content: noteText });
      toast.success("Note added");
      setNoteText("");
    } catch (e: any) { toast.error(e.message); }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !customer) return;
    setLogoUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `${customer.id}/${Date.now()}.${ext}`;
      if (customer.logo_path) {
        await supabase.storage.from("customer-logos").remove([customer.logo_path]);
      }
      const { error: uploadErr } = await supabase.storage.from("customer-logos").upload(path, file);
      if (uploadErr) throw uploadErr;
      const { data: urlData } = supabase.storage.from("customer-logos").getPublicUrl(path);
      await supabase.from("customers").update({ logo_url: urlData.publicUrl, logo_path: path }).eq("id", customer.id);
      toast.success("Logo updated");
      window.location.reload();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLogoUploading(false);
    }
  };

  if (isLoading) return <p className="text-muted-foreground">Loading…</p>;
  if (!customer) return <p className="text-muted-foreground">Customer not found.</p>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" className="-ml-2" onClick={() => navigate("/controller/customers")}>
            <ArrowLeft className="mr-1 h-4 w-4" />Back
          </Button>
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="mr-1 h-3 w-3" />Edit
          </Button>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          {/* Customer Logo */}
          <div className="relative group">
            <Avatar className="h-14 w-14">
              {customer.logo_url && <AvatarImage src={customer.logo_url} />}
              <AvatarFallback className="text-lg"><Building2 className="h-6 w-6" /></AvatarFallback>
            </Avatar>
            <button
              onClick={() => logoRef.current?.click()}
              disabled={logoUploading}
              className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Camera className="h-4 w-4 text-white" />
            </button>
            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              {customer.customer_number != null && (
                <span className="text-lg font-mono text-muted-foreground">#{customer.customer_number}</span>
              )}
              <h1 className="text-2xl font-bold tracking-tight">{customer.name}</h1>
              <Badge variant="secondary">{TYPE_LABELS[customer.customer_type || "other"]}</Badge>
              {syncBadge(customer.qb_sync_status)}
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mt-1">
              {customer.contact_name && <span className="flex items-center gap-1"><User className="h-3 w-3" />{customer.contact_name}</span>}
              {customer.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{customer.email}</span>}
              {customer.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{customer.phone}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: "Total Projects", value: stats.total },
          { label: "Active Projects", value: stats.active },
          { label: "Contract Value", value: fmt(stats.contractValue) },
          { label: "Total Billed", value: fmt(stats.billed) },
          { label: "Outstanding", value: fmt(stats.outstanding) },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{s.label}</p>
              <p className="text-lg font-semibold">{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <Tabs defaultValue={initialTab}>
        <TabsList>
          <TabsTrigger value="projects">Projects</TabsTrigger>
          <TabsTrigger value="estimates">Estimates</TabsTrigger>
          <TabsTrigger value="contacts">Contacts</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
        </TabsList>

        {/* PROJECTS TAB */}
        <TabsContent value="projects" className="space-y-4">
          <div className="flex items-center justify-between">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" onClick={() => setNewProjectOpen(true)}>
              <Plus className="mr-1 h-4 w-4" />New Project
            </Button>
          </div>
          {/* Mobile card view */}
          <div className="md:hidden space-y-3">
            {filteredProjects.map((p: any) => {
              const cv = (p.contracts || []).filter((c: any) => c.status === "approved").reduce((s: number, c: any) => s + Number(c.revised_value || c.original_value || 0), 0);
              const billedStatuses = ["approved", "sent", "paid", "partially_paid", "overdue"];
              const projectBilled = invoices?.filter((i: any) => i.project_id === p.id && billedStatuses.includes(i.status)).reduce((s: number, i: any) => s + Number(i.total || 0), 0) ?? 0;
              return (
                <Card key={p.id} className="cursor-pointer active:bg-muted/50" onClick={() => navigate(`/controller/projects/${p.id}`)}>
                  <CardContent className="p-4 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm font-medium">{p.project_number || "—"}</span>
                      <StatusBadge status={p.status} />
                    </div>
                    <p className="font-medium truncate">{p.name}</p>
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Contract: {fmt(cv)}</span>
                      <span>Billed: {fmt(projectBilled)}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {!filteredProjects.length && <p className="text-center text-muted-foreground py-8">No projects found.</p>}
          </div>
          {/* Desktop table */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project #</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Contract Value</TableHead>
                  <TableHead className="text-right">Billed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProjects.map((p: any) => {
                  const cv = (p.contracts || []).filter((c: any) => c.status === "approved").reduce((s: number, c: any) => s + Number(c.revised_value || c.original_value || 0), 0);
                  const billedStatuses = ["approved", "sent", "paid", "partially_paid", "overdue"];
                  const projectBilled = invoices?.filter((i: any) => i.project_id === p.id && billedStatuses.includes(i.status)).reduce((s: number, i: any) => s + Number(i.total || 0), 0) ?? 0;
                  return (
                    <TableRow key={p.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/controller/projects/${p.id}`)}>
                      <TableCell className="font-medium">{p.project_number || "—"}</TableCell>
                      <TableCell>{p.name}</TableCell>
                      <TableCell><StatusBadge status={p.status} /></TableCell>
                      <TableCell className="text-right">{fmt(cv)}</TableCell>
                      <TableCell className="text-right">{fmt(projectBilled)}</TableCell>
                    </TableRow>
                  );
                })}
                {!filteredProjects.length && (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">No projects found.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {filteredProjects.length > 0 && (
            <div className="flex flex-wrap justify-end gap-4 md:gap-6 text-sm font-medium border-t pt-3">
              <span>Contract: {fmt(stats.contractValue)}</span>
              <span>Billed: {fmt(stats.billed)}</span>
              <span>Outstanding: {fmt(stats.outstanding)}</span>
            </div>
          )}
        </TabsContent>

        {/* ESTIMATES TAB */}
        <TabsContent value="estimates" className="space-y-4">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => setNewEstimateOpen(true)}>
              <Plus className="mr-1 h-4 w-4" />New Estimate
            </Button>
          </div>
          {/* Mobile card view */}
          <div className="md:hidden space-y-3">
            {estimates?.map((e: any) => (
              <Card key={e.id}>
                <CardContent className="p-4 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm font-medium">{e.estimate_number || "—"}</span>
                    <StatusBadge status={e.status?.toLowerCase()} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground capitalize">{e.job_type || "—"}</span>
                    <span className="text-lg font-bold">{fmt(Number(e.total_amount || 0))}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{e.txn_date ? format(new Date(e.txn_date), "MMM d, yyyy") : "—"}</p>
                </CardContent>
              </Card>
            ))}
            {!estimates?.length && <p className="text-center text-muted-foreground py-8">No estimates found.</p>}
          </div>
          {/* Desktop table */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Estimate #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Job Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {estimates?.map((e: any) => (
                  <TableRow key={e.id}>
                    <TableCell className="font-medium">{e.estimate_number || "—"}</TableCell>
                    <TableCell>{e.txn_date ? format(new Date(e.txn_date), "MMM d, yyyy") : "—"}</TableCell>
                    <TableCell className="capitalize">{e.job_type || "—"}</TableCell>
                    <TableCell><StatusBadge status={e.status?.toLowerCase()} /></TableCell>
                    <TableCell className="text-right">{fmt(Number(e.total_amount || 0))}</TableCell>
                  </TableRow>
                ))}
                {!estimates?.length && (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">No estimates found.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* CONTACTS TAB */}
        <TabsContent value="contacts" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">Manage contacts for this customer. Different contacts can receive different documents.</p>
            <Button size="sm" onClick={() => setNewContactOpen(true)}>
              <Plus className="mr-1 h-4 w-4" />Add Contact
            </Button>
          </div>

          {/* Primary contact from customer record */}
          {customer.contact_name && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium">{customer.contact_name}</span>
                  <Badge variant="default" className="text-[10px]">Primary</Badge>
                </div>
                <div className="flex gap-4 text-sm text-muted-foreground">
                  {customer.email && <span>{customer.email}</span>}
                  {customer.phone && <span>{customer.phone}</span>}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Mobile card view */}
          <div className="md:hidden space-y-3">
            {contacts?.map((c) => (
              <Card key={c.id}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{c.name}</span>
                      {c.is_primary && <Badge variant="secondary" className="text-[9px]">Primary</Badge>}
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => deleteContact.mutateAsync({ id: c.id, customer_id: c.customer_id }).then(() => toast.success("Contact removed"))}>
                      Remove
                    </Button>
                  </div>
                  {c.title && <p className="text-sm text-muted-foreground">{c.title}</p>}
                  <div className="flex flex-wrap gap-3 text-sm">
                    {c.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{c.email}</span>}
                    {c.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone}</span>}
                  </div>
                  <div className="flex gap-3 text-xs text-muted-foreground">
                    <span>Estimates: {c.receives_estimates ? "✓" : "—"}</span>
                    <span>Invoices: {c.receives_invoices ? "✓" : "—"}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
            {!contacts?.length && <p className="text-center text-muted-foreground py-8">No additional contacts.</p>}
          </div>
          {/* Desktop table */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Estimates</TableHead>
                  <TableHead>Invoices</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts?.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.name}{c.is_primary && <Badge variant="secondary" className="ml-2 text-[9px]">Primary</Badge>}</TableCell>
                    <TableCell className="text-sm">{c.title || "—"}</TableCell>
                    <TableCell className="text-sm">{c.email || "—"}</TableCell>
                    <TableCell className="text-sm">{c.phone || "—"}</TableCell>
                    <TableCell>{c.receives_estimates ? "✓" : "—"}</TableCell>
                    <TableCell>{c.receives_invoices ? "✓" : "—"}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => deleteContact.mutateAsync({ id: c.id, customer_id: c.customer_id }).then(() => toast.success("Contact removed"))}>
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!contacts?.length && (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">No additional contacts.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* BILLING TAB */}
        <TabsContent value="billing" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <span className="text-muted-foreground">Outstanding Balance: </span>
              <span className="font-semibold text-lg">{fmt(stats.outstanding)}</span>
            </div>
          </div>
          {/* Mobile card view */}
          <div className="md:hidden space-y-3">
            {invoices?.map((inv: any) => (
              <Card key={inv.id}>
                <CardContent className="p-4 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm font-medium">{inv.invoice_number}</span>
                    <StatusBadge status={inv.status} />
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{inv.projects?.name || "—"}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{inv.invoice_date ? format(new Date(inv.invoice_date), "MMM d, yyyy") : "—"}</span>
                    <span className="text-lg font-bold">{fmt(Number(inv.total || 0))}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
            {!invoices?.length && <p className="text-center text-muted-foreground py-8">No invoices found.</p>}
          </div>
          {/* Desktop table */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices?.map((inv: any) => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                    <TableCell>{inv.projects?.name || "—"}</TableCell>
                    <TableCell>{inv.invoice_date ? format(new Date(inv.invoice_date), "MMM d, yyyy") : "—"}</TableCell>
                    <TableCell><StatusBadge status={inv.status} /></TableCell>
                    <TableCell className="text-right">{fmt(Number(inv.total || 0))}</TableCell>
                  </TableRow>
                ))}
                {!invoices?.length && (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">No invoices found.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* PAYMENTS TAB */}
        <TabsContent value="payments" className="space-y-4">
          {(() => {
            const paidInvoices = invoices?.filter((i: any) => i.status === "paid" || i.status === "partially_paid") ?? [];
            const totalPaid = paidInvoices.reduce((s: number, i: any) => s + Number(i.amount_paid || 0), 0);
            return (
              <>
                <div className="text-sm">
                  <span className="text-muted-foreground">Total Payments Received: </span>
                  <span className="font-semibold text-lg">{fmt(totalPaid)}</span>
                </div>
                {/* Mobile */}
                <div className="md:hidden space-y-3">
                  {paidInvoices.map((inv: any) => (
                    <Card key={inv.id}>
                      <CardContent className="p-4 space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-sm font-medium">{inv.invoice_number}</span>
                          <StatusBadge status={inv.status} />
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{inv.projects?.name || "—"}</p>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-xs text-muted-foreground capitalize">{(inv.payment_method || "—").replace("_", " ")}</span>
                          <span className="text-lg font-bold">{fmt(Number(inv.amount_paid || 0))}</span>
                        </div>
                        {inv.payment_reference && <p className="text-xs text-muted-foreground">Ref: {inv.payment_reference}</p>}
                      </CardContent>
                    </Card>
                  ))}
                  {!paidInvoices.length && <p className="text-center text-muted-foreground py-8">No payments received yet.</p>}
                </div>
                {/* Desktop */}
                <div className="hidden md:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Project</TableHead>
                        <TableHead>Payment Method</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Amount Paid</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paidInvoices.map((inv: any) => (
                        <TableRow key={inv.id}>
                          <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                          <TableCell>{inv.projects?.name || "—"}</TableCell>
                          <TableCell className="capitalize">{(inv.payment_method || "—").replace("_", " ")}</TableCell>
                          <TableCell>{inv.payment_reference || "—"}</TableCell>
                          <TableCell><StatusBadge status={inv.status} /></TableCell>
                          <TableCell className="text-right font-medium">{fmt(Number(inv.amount_paid || 0))}</TableCell>
                        </TableRow>
                      ))}
                      {!paidInvoices.length && (
                        <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No payments received yet.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </>
            );
          })()}
        </TabsContent>

        {/* NOTES TAB */}
        <TabsContent value="notes" className="space-y-4">
          <div className="flex gap-2">
            <Textarea
              placeholder="Add a note about this customer…"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              className="flex-1"
              rows={2}
            />
            <Button size="sm" className="self-end" onClick={handleAddNote} disabled={!noteText.trim() || createNote.isPending}>
              Add Note
            </Button>
          </div>
          <div className="space-y-3">
            {notes?.map((n) => (
              <Card key={n.id}>
                <CardContent className="p-4">
                  <p className="text-sm">{n.content}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {format(new Date(n.created_at), "MMM d, yyyy 'at' h:mm a")}
                  </p>
                </CardContent>
              </Card>
            ))}
            {!notes?.length && <p className="text-sm text-muted-foreground">No notes yet.</p>}
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Customer Dialog */}
      <NewCustomerDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        editCustomer={customer}
        onCreated={() => {}}
      />

      {/* New Estimate Dialog — customer pre-selected */}
      <NewEstimateDialog
        open={newEstimateOpen}
        onOpenChange={setNewEstimateOpen}
        defaultCustomerId={id}
      />

      {/* New Project Wizard */}
      <NewProjectWizard
        open={newProjectOpen}
        onOpenChange={setNewProjectOpen}
        defaultCustomerId={id}
      />

      {/* Add Contact Dialog */}
      <Dialog open={newContactOpen} onOpenChange={setNewContactOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input value={contactName} onChange={(e) => setContactName(e.target.value)} />
            </div>
            <div>
              <Label>Title</Label>
              <Input value={contactTitle} onChange={(e) => setContactTitle(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={contactReceivesEstimates} onCheckedChange={(v) => setContactReceivesEstimates(!!v)} />
                Receives Estimates
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={contactReceivesInvoices} onCheckedChange={(v) => setContactReceivesInvoices(!!v)} />
                Receives Invoices
              </label>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={contactNotes} onChange={(e) => setContactNotes(e.target.value)} rows={2} />
            </div>
            <Button className="w-full" onClick={handleAddContact} disabled={!contactName.trim() || createContact.isPending}>
              Add Contact
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerDetail;
